/**
 * Auth Client - better-auth SDK client configuration
 *
 * This module provides the configured auth client for authentication operations.
 * Used by auth-related stores (LoginStore, RegisterStore, etc.)
 */

import { emailOTPClient, oneTimeTokenClient } from 'better-auth/client/plugins'
import { createAuthClient } from 'better-auth/react'
import { tokenStorage, inIframe, apiBase } from 'txom-frontend-libs'

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

// Type definitions for methods not properly inferred by TypeScript
type AuthResponse<T> = Promise<{ data: T; error: null } | { data: null; error: { message: string } }>

interface EmailOtpMethods {
  verifyEmail: (params: { email: string; otp: string }) => AuthResponse<{ status: boolean }>
  sendVerificationOtp: (params: { email: string; type: 'sign-in' | 'email-verification' | 'forget-password' }) => AuthResponse<{ success: boolean }>
}

interface AuthClientExtensions {
  emailOtp: EmailOtpMethods
  requestPasswordReset: (params: { email: string; redirectTo: string }) => AuthResponse<{ status: boolean }>
  resetPassword: (params: { token: string; newPassword: string }) => AuthResponse<{ status: boolean }>
}

// ============================================================================
// AUTH CLIENT
// ============================================================================

const baseAuthClient = createAuthClient({
  basePath: apiBase + '/api/auth',
  plugins: [oneTimeTokenClient(), emailOTPClient()],
  fetchOptions: inIframe
    ? {
        // in iframe use token mode, avoid cross-origin cookie problem
        credentials: 'omit',
        onSuccess: (ctx) => {
          const token = ctx.response.headers.get('set-auth-token')
          if (token) tokenStorage.save(token)
        },
        auth: {
          type: 'Bearer',
          token: () => tokenStorage.get() || '',
        },
        customFetchImpl: (...args) => fetch(...args),
      }
    : {
        // not in iframe use cookie mode
        credentials: 'include',
        customFetchImpl: (...args) => fetch(...args),
      },
})

// Export with extended types (methods exist but TypeScript inference doesn't detect them)
export const authClient = baseAuthClient as typeof baseAuthClient & AuthClientExtensions
